'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { LucideIcon, MoreVertical, ChevronRight, Copy, Edit, Trash2, ExternalLink, AlertTriangle } from 'lucide-react'
import { motion } from 'framer-motion'
import { BirhausButton } from '../BirhausButton'
import { BirhausBadge } from '../BirhausBadge'

// BIRHAUS-specific interfaces
interface BirhausResourceCardAvatar {
  src?: string
  initials?: string
  icon?: LucideIcon
  fallbackColor?: 'default' | 'blue' | 'green' | 'purple' | 'orange' | 'pink'
}

interface BirhausResourceCardStatus {
  labelEs: string
  labelEn: string
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info'
}

interface BirhausResourceCardBadge {
  labelEs: string
  labelEn: string
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info'
}

interface BirhausResourceCardInfo {
  labelEs: string
  labelEn: string
  value: string | number
  icon?: LucideIcon
  highlight?: boolean
  format?: 'text' | 'number' | 'currency' | 'date' | 'phone' | 'email'
}

interface BirhausResourceCardAction {
  keyEs: string
  keyEn: string
  labelEs: string
  labelEn: string
  onClick: () => void
  icon?: LucideIcon
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive'
  condition?: (data: any) => boolean
  divider?: boolean
}

export interface BirhausResourceCardProps {
  // Core Properties
  id: string
  titleEs: string
  titleEn: string
  subtitleEs?: string
  subtitleEn?: string
  descriptionEs?: string
  descriptionEn?: string
  avatar?: BirhausResourceCardAvatar
  
  // Status and Metadata
  status?: BirhausResourceCardStatus
  badges?: BirhausResourceCardBadge[]
  
  // Information Sections
  primaryInfo?: BirhausResourceCardInfo[]
  secondaryInfo?: BirhausResourceCardInfo[]
  
  // Actions
  primaryAction?: BirhausResourceCardAction
  menuActions?: BirhausResourceCardAction[]
  onCardClick?: () => void
  
  // Visual Options
  variant?: 'default' | 'elevated' | 'interactive' | 'compact'
  layout?: 'vertical' | 'horizontal'
  showAvatar?: boolean
  
  // BIRHAUS-specific props
  maxPrimaryInfo?: number // Miller's Law enforcement
  maxSecondaryInfo?: number
  maxMenuActions?: number
  showCognitiveWarning?: boolean
  cognitiveMode?: 'strict' | 'warning' | 'disabled'
  
  // Styling
  className?: string
  
  // Accessibility
  'aria-label'?: string
  'aria-describedby'?: string
  role?: string
  
  // Data context (for conditional actions)
  data?: any
}

export function BirhausResourceCard({
  id,
  titleEs,
  titleEn,
  subtitleEs,
  subtitleEn,
  descriptionEs,
  descriptionEn,
  avatar,
  status,
  badges = [],
  primaryInfo = [],
  secondaryInfo = [],
  primaryAction,
  menuActions = [],
  onCardClick,
  variant = 'default',
  layout = 'vertical',
  showAvatar = true,
  maxPrimaryInfo = 6,
  maxSecondaryInfo = 8,
  maxMenuActions = 5,
  showCognitiveWarning = false,
  cognitiveMode = 'warning',
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  role,
  data,
  ...props
}: BirhausResourceCardProps) {
  const [isHovered, setIsHovered] = React.useState(false)
  const isClickable = !!onCardClick

  // BIRHAUS: Cognitive load validation
  const cognitiveWarnings = React.useMemo(() => {
    if (cognitiveMode === 'disabled') return []
    
    const warnings: string[] = []
    
    if (primaryInfo.length > maxPrimaryInfo) {
      warnings.push(`Información primaria excede límite (${primaryInfo.length}/${maxPrimaryInfo})`)
    }
    if (secondaryInfo.length > maxSecondaryInfo) {
      warnings.push(`Información secundaria excede límite (${secondaryInfo.length}/${maxSecondaryInfo})`)
    }
    if (menuActions.length > maxMenuActions) {
      warnings.push(`Acciones de menú exceden límite (${menuActions.length}/${maxMenuActions})`)
    }
    if (badges.length > 4) {
      warnings.push(`Badges exceden límite recomendado (${badges.length}/4)`)
    }
    
    return warnings
  }, [primaryInfo.length, secondaryInfo.length, menuActions.length, badges.length, maxPrimaryInfo, maxSecondaryInfo, maxMenuActions, cognitiveMode])

  // BIRHAUS: Console warnings in strict mode
  React.useEffect(() => {
    if (cognitiveMode === 'strict' && cognitiveWarnings.length > 0) {
      console.warn('BIRHAUS ResourceCard: Cognitive load issues detected:', cognitiveWarnings)
    }
  }, [cognitiveWarnings, cognitiveMode])

  const shouldShowCognitiveWarning = showCognitiveWarning || (cognitiveWarnings.length > 0 && cognitiveMode !== 'disabled')
  
  const variantClasses = {
    default: 'bg-white border-gray-200',
    elevated: 'bg-white border-gray-200 shadow-md',
    interactive: 'bg-white border-gray-200 hover:shadow-lg transition-shadow cursor-pointer',
    compact: 'bg-gray-50 border-gray-100'
  }

  const avatarColors = {
    default: 'from-gray-600 to-gray-700',
    blue: 'from-blue-600 to-blue-700',
    green: 'from-green-600 to-green-700',
    purple: 'from-purple-600 to-purple-700',
    orange: 'from-orange-600 to-orange-700',
    pink: 'from-pink-600 to-pink-700'
  }
  
  const formatValue = (value: string | number, format?: BirhausResourceCardInfo['format']) => {
    if (!format || format === 'text') return value
    
    switch (format) {
      case 'number':
        return typeof value === 'number' ? value.toLocaleString('es-PY') : value
      case 'currency':
        return typeof value === 'number' 
          ? new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(value)
          : value
      case 'date':
        return typeof value === 'string' 
          ? new Date(value).toLocaleDateString('es-PY')
          : value
      case 'phone':
        return typeof value === 'string' && value.startsWith('+') ? value : `+595 ${value}`
      case 'email':
        return value
      default:
        return value
    }
  }
  
  const renderAvatar = () => {
    if (!showAvatar || !avatar) return null
    
    const AvatarIcon = avatar.icon
    const colorClass = avatarColors[avatar.fallbackColor || 'default']
    
    return (
      <div className={cn(
        'flex-shrink-0 rounded-full flex items-center justify-center text-white',
        `bg-gradient-to-br ${colorClass}`,
        layout === 'horizontal' ? 'w-16 h-16' : 'w-12 h-12'
      )}>
        {avatar.src ? (
          <img
            src={avatar.src}
            alt={titleEs}
            className="w-full h-full rounded-full object-cover"
          />
        ) : AvatarIcon ? (
          <AvatarIcon className={layout === 'horizontal' ? 'w-8 h-8' : 'w-6 h-6'} />
        ) : (
          <span className={cn(
            'font-semibold',
            layout === 'horizontal' ? 'text-lg' : 'text-sm'
          )}>
            {avatar.initials || titleEs.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </span>
        )}
      </div>
    )
  }
  
  const renderHeader = () => (
    <div className={cn(
      'flex items-start gap-4',
      layout === 'horizontal' && 'flex-1'
    )}>
      {renderAvatar()}
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              'font-semibold text-gray-900 truncate',
              variant === 'compact' ? 'text-sm' : 'text-base'
            )}>
              {titleEs}
            </h3>
            {subtitleEs && (
              <p className="text-sm text-gray-600 truncate mt-0.5">{subtitleEs}</p>
            )}
          </div>
          
          {/* Status Badge */}
          {status && (
            <BirhausBadge 
              variant={status.variant || 'default'}
              className="flex-shrink-0"
            >
              {status.labelEs}
            </BirhausBadge>
          )}
        </div>
        
        {/* Description */}
        {descriptionEs && layout === 'vertical' && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{descriptionEs}</p>
        )}
        
        {/* Additional Badges (BIRHAUS: Limited to 4) */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {badges.slice(0, 4).map((badge, index) => (
              <BirhausBadge 
                key={index}
                variant={badge.variant || 'default'}
                className="text-xs"
              >
                {badge.labelEs}
              </BirhausBadge>
            ))}
            {badges.length > 4 && (
              <BirhausBadge variant="default" className="text-xs">
                +{badges.length - 4} más
              </BirhausBadge>
            )}
          </div>
        )}
      </div>
    </div>
  )
  
  const renderInfo = () => {
    const displayPrimaryInfo = primaryInfo.slice(0, maxPrimaryInfo)
    const displaySecondaryInfo = secondaryInfo.slice(0, maxSecondaryInfo)
    
    if (displayPrimaryInfo.length === 0 && displaySecondaryInfo.length === 0) return null
    
    return (
      <div className={cn(
        'space-y-3',
        layout === 'horizontal' && 'flex gap-6 space-y-0'
      )}>
        {/* Primary Info */}
        {displayPrimaryInfo.length > 0 && (
          <div className={cn(
            'grid gap-2',
            displayPrimaryInfo.length > 2 ? 'grid-cols-2' : 'grid-cols-1',
            layout === 'horizontal' && 'flex gap-4 flex-1'
          )}>
            {displayPrimaryInfo.map((info, index) => {
              const InfoIcon = info.icon
              return (
                <div 
                  key={index}
                  className={cn(
                    'flex items-center gap-2',
                    info.highlight && 'text-blue-600 font-medium'
                  )}
                >
                  {InfoIcon && <InfoIcon className="w-4 h-4 text-gray-400" />}
                  <div className="text-sm">
                    <span className="text-gray-600">{info.labelEs}:</span>
                    <span className="ml-1 font-medium">
                      {formatValue(info.value, info.format)}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        
        {/* Secondary Info */}
        {displaySecondaryInfo.length > 0 && (
          <div className={cn(
            'pt-3 border-t border-gray-100',
            layout === 'horizontal' && 'border-t-0 border-l pl-6'
          )}>
            <div className="grid grid-cols-1 gap-1.5">
              {displaySecondaryInfo.map((info, index) => {
                const InfoIcon = info.icon
                return (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    {InfoIcon && <InfoIcon className="w-3.5 h-3.5 text-gray-400" />}
                    <span className="text-gray-500">{info.labelEs}:</span>
                    <span className="text-gray-700">{formatValue(info.value, info.format)}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        
        {/* Truncation indicators */}
        {primaryInfo.length > maxPrimaryInfo && (
          <div className="text-xs text-gray-500 italic">
            +{primaryInfo.length - maxPrimaryInfo} campos adicionales
          </div>
        )}
        {secondaryInfo.length > maxSecondaryInfo && (
          <div className="text-xs text-gray-500 italic">
            +{secondaryInfo.length - maxSecondaryInfo} detalles adicionales
          </div>
        )}
      </div>
    )
  }
  
  const renderActions = () => {
    const displayMenuActions = menuActions.slice(0, maxMenuActions)
    
    if (!primaryAction && displayMenuActions.length === 0) return null
    
    return (
      <div className={cn(
        'flex items-center gap-2',
        layout === 'horizontal' ? 'ml-auto pl-4' : 'pt-4 border-t border-gray-100'
      )}>
        {primaryAction && (
          <BirhausButton
            onClick={(e) => {
              e.stopPropagation()
              primaryAction.onClick()
            }}
            variant={primaryAction.variant || 'primary'}
            size="sm"
            className="flex-1"
          >
            {primaryAction.icon && <primaryAction.icon className="w-4 h-4 mr-1.5" />}
            {primaryAction.labelEs}
          </BirhausButton>
        )}
        
        {displayMenuActions.length > 0 && (
          <div className="relative group">
            <button
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'p-2 rounded-lg transition-colors',
                'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
              )}
              aria-label="Más acciones"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            
            {/* Simple dropdown menu - in real implementation would use a proper dropdown */}
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              {displayMenuActions.map((action, index) => {
                const ActionIcon = action.icon
                const shouldShow = !action.condition || action.condition(data)
                
                if (!shouldShow) return null
                
                return (
                  <React.Fragment key={index}>
                    {action.divider && index > 0 && <div className="border-t border-gray-100" />}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        action.onClick()
                      }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg',
                        action.variant === 'destructive' && 'text-red-600 hover:text-red-700 hover:bg-red-50'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {ActionIcon && <ActionIcon className="w-4 h-4" />}
                        {action.labelEs}
                      </div>
                    </button>
                  </React.Fragment>
                )
              })}
              
              {menuActions.length > maxMenuActions && (
                <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100">
                  +{menuActions.length - maxMenuActions} acciones más
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const cardContent = (
    <>
      {/* BIRHAUS: Cognitive Load Warning */}
      {shouldShowCognitiveWarning && cognitiveMode !== 'disabled' && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            <p className="font-medium mb-1">Advertencia cognitiva</p>
            <ul className="space-y-1">
              {cognitiveWarnings.map((warning, index) => (
                <li key={index}>• {warning}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {layout === 'vertical' ? (
        <div className="space-y-4">
          {renderHeader()}
          {renderInfo()}
          {renderActions()}
        </div>
      ) : (
        <div className="flex items-start gap-4">
          {renderHeader()}
          {renderInfo()}
          {renderActions()}
        </div>
      )}
    </>
  )
  
  const cardClasses = cn(
    'rounded-xl border p-6 transition-all duration-200',
    variantClasses[variant],
    isClickable && 'cursor-pointer',
    isHovered && variant === 'interactive' && 'scale-[1.02]',
    className
  )
  
  if (isClickable) {
    return (
      <motion.div
        whileHover={{ y: variant === 'interactive' ? -2 : 0 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={onCardClick}
        className={cardClasses}
        aria-label={ariaLabel || `Tarjeta de ${titleEs}`}
        aria-describedby={ariaDescribedBy}
        role={role || 'button'}
        {...props}
      >
        {cardContent}
        {variant === 'interactive' && (
          <ChevronRight className={cn(
            'absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400',
            'transition-transform duration-200',
            isHovered && 'translate-x-1'
          )} />
        )}
      </motion.div>
    )
  }
  
  return (
    <div 
      className={cardClasses}
      aria-label={ariaLabel || `Información de ${titleEs}`}
      aria-describedby={ariaDescribedBy}
      role={role}
      {...props}
    >
      {cardContent}
    </div>
  )
}

// Pre-configured templates for common entity types with BIRHAUS principles
export const BirhausResourceCardTemplates = {
  Student: (props: Partial<BirhausResourceCardProps>) => ({
    showAvatar: true,
    layout: 'vertical' as const,
    variant: 'elevated' as const,
    maxPrimaryInfo: 4, // Miller's Law compliance
    maxSecondaryInfo: 6,
    maxMenuActions: 4,
    cognitiveMode: 'warning' as const,
    menuActions: [
      { 
        keyEs: 'profile', keyEn: 'profile',
        labelEs: 'Ver perfil', labelEn: 'View profile',
        icon: ExternalLink, onClick: () => {} 
      },
      { 
        keyEs: 'edit', keyEn: 'edit',
        labelEs: 'Editar', labelEn: 'Edit',
        icon: Edit, onClick: () => {} 
      },
      { 
        keyEs: 'copy', keyEn: 'copy',
        labelEs: 'Copiar ID', labelEn: 'Copy ID',
        icon: Copy, onClick: () => {} 
      },
      { 
        keyEs: 'delete', keyEn: 'delete',
        labelEs: 'Eliminar', labelEn: 'Delete',
        icon: Trash2, variant: 'destructive' as const, divider: true, onClick: () => {} 
      }
    ],
    ...props
  }),
  
  Employee: (props: Partial<BirhausResourceCardProps>) => ({
    showAvatar: true,
    layout: 'horizontal' as const,
    variant: 'default' as const,
    maxPrimaryInfo: 5,
    maxSecondaryInfo: 7,
    maxMenuActions: 3,
    cognitiveMode: 'warning' as const,
    menuActions: [
      { 
        keyEs: 'schedule', keyEn: 'schedule',
        labelEs: 'Ver horario', labelEn: 'View schedule',
        icon: ExternalLink, onClick: () => {} 
      },
      { 
        keyEs: 'edit', keyEn: 'edit',
        labelEs: 'Editar', labelEn: 'Edit',
        icon: Edit, onClick: () => {} 
      },
      { 
        keyEs: 'access', keyEn: 'access',
        labelEs: 'Gestionar acceso', labelEn: 'Manage access',
        icon: ExternalLink, onClick: () => {} 
      }
    ],
    ...props
  }),
  
  Course: (props: Partial<BirhausResourceCardProps>) => ({
    showAvatar: false,
    layout: 'vertical' as const,
    variant: 'interactive' as const,
    maxPrimaryInfo: 5,
    maxSecondaryInfo: 5,
    maxMenuActions: 3,
    cognitiveMode: 'warning' as const,
    primaryAction: {
      keyEs: 'details', keyEn: 'details',
      labelEs: 'Ver detalles', labelEn: 'View details',
      onClick: () => {},
      variant: 'secondary' as const
    },
    ...props
  }),
  
  Contract: (props: Partial<BirhausResourceCardProps>) => ({
    showAvatar: false,
    layout: 'horizontal' as const,
    variant: 'compact' as const,
    maxPrimaryInfo: 4,
    maxSecondaryInfo: 4,
    maxMenuActions: 2,
    cognitiveMode: 'strict' as const,
    menuActions: [
      { 
        keyEs: 'payments', keyEn: 'payments',
        labelEs: 'Ver pagos', labelEn: 'View payments',
        icon: ExternalLink, onClick: () => {} 
      },
      { 
        keyEs: 'print', keyEn: 'print',
        labelEs: 'Imprimir', labelEn: 'Print',
        icon: ExternalLink, onClick: () => {} 
      }
    ],
    ...props
  })
} as const